import { afterPageLoad } from './page-load.js';
import { analytics } from './analytics.js';
import { createArticleLinkFallback, isArticleAnswer } from './article-link-fallback.js';
import { contrastFor, isHexColor } from './color.js';
import { createButton, createPanel } from './chrome.js';
import { createConfigRevalidation } from './surface-revalidation.js';
import { createFrameLifecycle } from './surface-frame.js';
import { createInboxSurface } from './surface-inbox.js';
import { createPanelControls } from './surface-controls.js';
import { createProactiveSurfaces } from './surface-proactive.js';
import { createTrustSurface } from './surface-trust.js';
import { defaultAccent } from './constants.js';
import { fetchConfig } from './config.js';
import { watchHostOverlays } from './host-overlays.js';
import { injectSparkleStyles } from './styles.js';
import { origin, snippetInstalled } from './snippet.js';
import { readPageOutline } from './page-reader.js';
import { resolvePageQueryKeys } from './resolve-nudges.js';
import { resolveOptions } from './resolve-options.js';
import { runtime } from './runtime.js';
import { markVisitorWrote } from './visit-marks.js';

/* Heroicons "x-mark", the same outline the app draws through HeroOutlineIconName.XMark. Inlined because
 * the loader cannot reach the app's icon registry, and stroked with currentColor so the stylesheet keeps
 * owning the color. */
export const xMarkIconSvg =
    '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2" xmlns="http://www.w3.org/2000/svg">' +
    '<path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>' +
    '</svg>';

/* 28px in the 48px circle — deliberately larger than a standard 24px icon, so the sparkles fill the
 * launcher the way the reference design does without growing the circle itself. */
export const chatIconSvg =
    '<svg aria-hidden="true" width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path class="lw-sp-l" d="M9 2C9 2 10.5 7 12 9C13.5 11 18 11.5 18 11.5C18 11.5 13.5 12 12 14C10.5 16 9 21 9 21C9 21 7.5 16 6 14C4.5 12 0 11.5 0 11.5C0 11.5 4.5 11 6 9C7.5 7 9 2 9 2Z" fill="currentColor"/>' +
    '<path class="lw-sp-m" d="M18.5 2C18.5 2 19.2 4.5 20 5.5C20.8 6.5 23 7 23 7C23 7 20.8 7.5 20 8.5C19.2 9.5 18.5 12 18.5 12C18.5 12 17.8 9.5 17 8.5C16.2 7.5 14 7 14 7C14 7 16.2 6.5 17 5.5C17.8 4.5 18.5 2 18.5 2Z" fill="currentColor"/>' +
    '<path class="lw-sp-s" d="M16.5 14C16.5 14 17 15.8 17.5 16.5C18 17.2 20 17.5 20 17.5C20 17.5 18 17.8 17.5 18.5C17 19.2 16.5 21 16.5 21C16.5 21 16 19.2 15.5 18.5C15 17.8 13 17.5 13 17.5C13 17.5 15 17.2 15.5 16.5C16 15.8 16.5 14 16.5 14Z" fill="currentColor"/>' +
    '</svg>';

/* Intercom convention: while the panel is open the launcher shows a "collapse" chevron. */
export const closeIconSvg =
    '<svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="m6 9 6 6 6-6"/>' +
    '</svg>';

function init(config) {
    if (document.getElementById('leadwin-agent-button')) {
        return; // A concurrent snippet won the race while the config was loading.
    }

    /* What the surfaces below share across their boundaries: the frame, the trust corner, the bubble
     * stack, and the handovers waiting for the frame to report ready. Everything a single surface
     * keeps to itself stays a closure variable next to the code that owns it — only what genuinely
     * crosses a boundary lives here, which is why this object is short and dull.
     *
     * A plain object rather than five closure variables, because the surfaces below are factories:
     * handed a `let`, each would capture the value of the moment and the next reassignment — a
     * remounted frame, a rebuilt corner — would be visible to the assigning side alone. */
    const state = {};

    const options = resolveOptions(config);

    // Published to the shared runtime, because the frame URL is built in runtime.js and the JS API is
    // answered in the panel controls — both have to know which languages the page has a say in (see
    // effectiveLanguage). The config that carries them has already been fetched in the page's own
    // language; a widget that does not serve it answers in its standard language regardless, which is
    // exactly why the values travel.
    runtime.defaultLanguage = options.language;
    runtime.contentLanguages = options.languages;

    // What counts as a page on this site. On runtime rather than in options because the page identity is
    // built one layer below the instance — the history hook that notices a new screen has no instance to
    // ask. Read straight from the payload and never from the bubble half of it: a live save replaces that
    // half wholesale and would collapse every screen back onto its path.
    runtime.pageQueryKeys = resolvePageQueryKeys(config);

    // Same reason as the line above: the waiting for a page's question happens in page-coverage.js
    // (the report is sent from there), while the list a question has to land in lives here.
    runtime.servePageMessage = function (entry) {
        options.pageMessages.push(entry);
    };

    injectSparkleStyles();
    const panel = createPanel(options);
    const button = createButton(options);
    /* The corner's rotating proof — its own surface now; see createTrustSurface. Built here
     * because the trust state it fills is read by the config re-read and by the panel, and because a
     * corner that resolves to nothing must cost the page no element at all. */
    const trust = createTrustSurface({
        options, button, state, config,
        // Late-bound, like applyLiveTrust below: the controls are constructed last, and the corner
        // only reaches for them when a visitor taps a card or a save repaints it.
        setOpen: (open) => controls.setOpen(open),
        requestScreen: (screen) => controls.requestScreen(screen),
        repaintForTheme: () => controls.repaintForTheme(),
        // Late-bound on purpose, and the one place the two surfaces genuinely need each other: the
        // corner is built before the re-read exists, but only reaches for it when a visitor dismisses
        // a group — long after both are up. Passing revalidation.applyLiveTrust directly here would
        // read it before the const is initialised.
        applyLiveTrust: (data) => revalidation.applyLiveTrust(data)
    });

    analytics.onImpression(); // The launcher is on the page — that is the funnel's impression.

    trust.install();

    /* The re-read of the widget's own settings and the live-save broadcast — its own surface now;
     * see createConfigRevalidation. Constructed here because it fingerprints the config this page
     * load was built from, and that has to happen before anything can change it. */
    const revalidation = createConfigRevalidation({
        config, state, options, panel, button, trust,
        repaintForTheme: () => controls.repaintForTheme(),
        // Late-bound like the line above: a save broadcast arrives long after every surface is up,
        // but the re-read is constructed before the corner and the controls exist.
        applyLiveNudges: (message) => proactive.applyLiveNudges(message)
    });

    /* Server-reachability recovery, the mount and the ready handshake — its own surface now; see
     * createFrameLifecycle. Constructed at the point the frame state used to be declared, so the
     * instance still comes up in the order it did. */
    const frame = createFrameLifecycle({
        state, panel,
        hostOverrideApplies: () => controls.hostOverrideApplies()
    });

    /* The bubbles, the page question and the campaigns — their own surface now; see
     * createProactiveSurfaces. It reaches the frame through setOpen and the shared state rather than
     * through the frame surface itself, which is why it needs nothing from it here. */
    const proactive = createProactiveSurfaces({
        state, options, panel, button, trust,
        // Late-bound: the controls are constructed after the corner, which only calls them when a
        // visitor clicks a bubble or a campaign.
        setOpen: (open) => controls.setOpen(open),
        openScreen: (screen) => controls.openScreen(screen),
        effectiveTheme: () => controls.effectiveTheme()
    });

    /* The red dot and the card for a waiting team reply; see createInboxSurface. After the bubbles,
     * whose slot it takes over, and before the controls, which repaint it on every open and close. */
    const inbox = createInboxSurface({
        state, options, button, trust, proactive,
        setOpen: (open) => controls.setOpen(open),
        effectiveTheme: () => controls.effectiveTheme()
    });

    /* Opening, screens, look and language — their own surface now; see createPanelControls. Last
     * to be constructed, because it is the only one that reads from three of the others; they reach
     * back into it through the late-bound arrows above. */
    const controls = createPanelControls({
        state, options, panel, button, trust, frame, proactive, inbox,
        // Late-bound: only called once a visitor closes the panel or opens something in it.
        forgetArticleLink: () => articleLinks.forget()
    });

    /* The help links' way back to their own pages, in a module of its own: see createArticleLinkFallback.
     * After the controls, which it opens and closes the panel through. */
    const articleLinks = createArticleLinkFallback({
        state, button,
        openArticle: (key, requestId) => controls.openArticle(key, requestId),
        setOpen: (open) => controls.setOpen(open),
        forgetPendingArticle: () => controls.forgetPendingArticle(),
        forgetArticleRequest: (requestId) => controls.forgetArticleRequest(requestId)
    });

    /**
     * The real LeadWin function (the install snippet's stub queues into it until this replaces it).
     *
     * This is one half of the public surface: what the page can tell the widget. The other half is what
     * the widget tells the page, and it is a single event on the document — see panelStateEventName in
     * constants.js, and the family rules written above it. Nothing else travels outwards, and nothing the
     * loader does reaches into the host's own DOM: what happens on a page belongs to whoever owns it.
     * @param {unknown} command - 'open' | 'close' | 'toggle' | 'article' | 'ask' | 'review' | 'project'
     *                            | 'theme' | 'language' | 'consent' | 'refresh'
     * @param {unknown} arg - Command argument, see the header comment
     */
    function dispatchApiCommand(command, arg) {
        try {
            if (!button.isConnected) {
                return; // The host application replaced the document — this instance is over.
            }

            switch (typeof command === 'string' ? command.trim().toLowerCase() : '') {
                case 'open':
                    controls.openScreen(controls.readScreen(arg));
                    break;
                case 'close':
                    controls.setOpen(false);
                    break;
                case 'toggle':
                    controls.setOpen(!state.panelOpen);
                    break;
                case 'article':
                    // Opens the panel straight on one Help-Center article — the host page's own "how does
                    // this work?" control, answered inside the widget instead of on a page away from it.
                    controls.openArticle(arg);
                    break;
                case 'ask':
                    // Opens the chat on one question asked in the visitor's name — the host page's own
                    // button instead of a sentence the visitor would have to type out. It joins the open
                    // conversation unless the object form asks for one of its own ({newChat: true}).
                    controls.askPrompt(arg);
                    break;
                case 'review':
                case 'project':
                    // Opens the panel on one review or one project, by the GUID key the corner's own
                    // cards carry: the host page's review and project tiles, answered by the widget's
                    // review and case-study drawers instead of a page away from it.
                    trust.openEvidenceByKey(command.trim().toLowerCase(), arg);
                    break;
                case 'theme':
                    controls.applyHostTheme(arg);
                    break;
                case 'language':
                    controls.applyHostLanguage(arg);
                    break;
                case 'refresh':
                    // The page says its settings moved — re-read the config and repaint the corner. For a
                    // page that carries both the settings and the launcher, this is the only signal there
                    // is: nothing blurs, and the circuit broadcast needs an opened chat.
                    revalidation.refreshConfig();
                    break;
                case 'consent':
                    // The one way a cookie banner hands its decision over (see the consent card).
                    analytics.setConsent(arg);
                    break;
                default:
                    break; // Unknown command — a newer snippet against an older loader stays harmless.
            }
        } catch { /* the API must never throw into the host page */ }
    }

    /** Replaces the install snippet's queueing stub with the real function and replays what it held. */
    function installApi() {
        let queued = null;

        try {
            const stub = globalThis.LeadWin;
            queued = stub && Array.isArray(stub.q) ? stub.q : null;
            globalThis.LeadWin = dispatchApiCommand;
        } catch {
            return; // Host page owns its globals — without ours there is nothing to replay either.
        }

        for (const args of queued ?? []) {
            dispatchApiCommand(...args);
        }
    }

    /**
     * The API without any JS: a click on (or inside) an element carrying data-leadwin-open opens the
     * widget, on the screen the attribute names — an empty value means the chat itself. Delegated, so
     * buttons rendered later (CMS blocks, SPA rerenders) work without wiring anything up.
     *
     * Its sister data-leadwin-article opens one Help-Center article. It sits on a real link to that
     * article's public page, whose address names the article (see article-links.js), and the link stays
     * the fallback: where the widget cannot take the article, or the visitor asked for a new tab, the
     * click simply navigates.
     */
    function hookDeclarativeOpens() {
        document.addEventListener('click', function (event) {
            const target = event.target;

            if (!target || typeof target.closest !== 'function') {
                return;
            }

            const articleLink = target.closest('[data-leadwin-article]');

            if (articleLink) {
                articleLinks.open(articleLink, event);
                return;
            }

            const trigger = target.closest('[data-leadwin-open]');

            if (trigger) {
                dispatchApiCommand('open', trigger.dataset.leadwinOpen || '');
            }
        });
    }

    button.addEventListener('click', function () {
        controls.setOpen(!state.panelOpen);
    });

    // A heartbeat response may carry the "a conversation is waiting" flag for a visitor whose widget
    // was never opened (no iframe, no circuit) — this is the only channel that reaches them.
    analytics.onOpenRequested(function () {
        if (button.isConnected && !state.panelOpen) {
            controls.setOpen(true);
        }
    });

    // Before the bubbles, and deliberately not inside their scheduling: a visitor who dismissed the
    // bubbles for good, or one on a phone the widget stays off, is still standing on a page whose
    // question the NEXT visitor should get.
    proactive.requestPageCoverageIfMissing();

    proactive.scheduleNudges(options.nudges, options.nudgeDelayMs, options.nudgeEveryPage);
    proactive.hookNudgeRouteChanges();
    proactive.hookMobileVisibility();
    // The page's own drawers and dialogs sit below every surface of the loader; while one is open the
    // trust corner steps back. The bubbles stay (host-overlays.js).
    watchHostOverlays();
    // A reply that waited for this visitor since their last visit. After the bubbles are scheduled, so a
    // card that arrives takes their slot rather than being skipped for it.
    inbox.loadOnPageLoad();
    hookDeclarativeOpens();
    installApi();

    // Messages posted up by the widget page: its close button collapses the panel, and live
    // branding changes (agency saved a new accent) recolor the launcher without any reload.
    window.addEventListener('message', function (event) {
        if (event.origin !== origin || !event.data) {
            return;
        }

        // A host application that replaced the document leaves this instance behind with detached
        // elements. The launcher is what everything here hangs off, so its absence ends the instance.
        if (!button.isConnected) {
            return;
        }

        if (event.data.type === 'feedbax:widget:close') {
            controls.setOpen(false);
        } else if (event.data.type === 'feedbax:widget:open') {
            // A contractor started a conversation with this visitor; the panel presents it.
            if (!state.panelOpen) {
                controls.setOpen(true);
            }
        } else if (event.data.type === 'feedbax:widget:expand') {
            // The visitor opened a help-center article, which reads badly in a 410px column. Only a
            // boolean travels and it can only ever pick between the two sizes this page defines, so a
            // forged message can at most resize a panel the visitor already has open.
            controls.setExpanded(event.data.expanded === true);
        } else if (isArticleAnswer(event.data.type)) {
            // The frame's one answer to an article request: shown, unavailable or dismissed. Only the help
            // link still waiting under that request id acts on it, so a forged answer can at most send the
            // visitor where that link was going anyway, or keep it from going.
            articleLinks.settle(event.data);
        } else if (event.data.type === 'feedbax:widget:ready') {
            frame.handleFrameReady();
        } else if (event.data.type === 'feedbax:widget:reloading') {
            // The page is about to navigate away from itself; the dots cover the frame until the new
            // document says ready. No data travels, so a forged message can at most show the dots.
            frame.handleFrameReloading();
            // The circuit a help link's article went to goes with the old document, and its answer too.
            articleLinks.giveUp();
        } else if (event.data.type === 'feedbax:widget:subscribed') {
            // The frame's circuit takes API messages from here on — replay what arrived while it was
            // still starting: the host page's theme override, and the screen a button asked for.
            state.frameSubscribed = true;
            // A cold start has spent the help link's patience on booting; the article load gets its own.
            articleLinks.renewPatience();
            state.frameDocumentLive = true; // A circuit that subscribes is proof enough on its own.
            if (state.hostTheme) {
                controls.postThemeToFrame();
            }
            if (state.pendingScreen) {
                controls.postScreenToFrame(state.pendingScreen);
                state.pendingScreen = null;
            }
            if (state.pendingEvidence) {
                trust.postEvidenceToFrame(state.pendingEvidence);
                state.pendingEvidence = null;
            }
            if (state.pendingArticleId) {
                controls.postArticleToFrame(state.pendingArticleId, state.pendingArticleRequestId);
                state.pendingArticleId = null;
                state.pendingArticleRequestId = null;
            }
            if (state.pendingPrompt) {
                controls.postPromptToFrame(state.pendingPrompt);
                state.pendingPrompt = null;
            }
            if (state.pendingBubble) {
                proactive.postBubbleToFrame(state.pendingBubble);
                state.pendingBubble = null;
            }
            if (state.pendingConversationId) {
                inbox.postConversationToFrame(state.pendingConversationId);
                state.pendingConversationId = null;
            }
            // What this visitor has switched the corner off for. Sent unconditionally, empty list
            // included: the widget's way back is drawn from it, and "nothing is hidden" is an answer.
            trust.postTrustHiddenToFrame();

            // The bubbles carry the same standing "not again", under a storage key of their own — and
            // until this arrived they carried no way back at all: the chip that switches them off sits
            // on a foreign website, and nothing in the widget knew it had been pressed.
            proactive.postNudgeOptOutToFrame();
        } else if (event.data.type === 'feedbax:widget:readpage') {
            // The circuit is sending a message and wants the page the visitor has open, as their browser
            // renders it right now. Read fresh on every ask rather than once on open: an accordion the
            // visitor unfolded and a route a single-page app switched to are both what the assistant
            // should see. Only the frame this instance mounted is answered, and only on the app origin.
            if (state.iframe && state.iframe.contentWindow) {
                state.iframe.contentWindow.postMessage(
                    { type: 'feedbax:widget:hostpage', text: readPageOutline() }, origin);
            }
        } else if (event.data.type === 'feedbax:widget:nudgerestore') {
            // The visitor asked for the bubbles back, through the widget's header menu. Nothing travels
            // but the request itself, so a forged message can at most undo a choice this very visitor
            // made — and the corner it brings back is the one the widget was configured to show anyway.
            proactive.applyNudgeRestore();
        } else if (event.data.type === 'feedbax:widget:visitorwrote') {
            // The visitor wrote in the chat: closing it no longer brings the bubbles back in this visit.
            // A forged message can at most keep a bubble away.
            markVisitorWrote();
        } else if (event.data.type === 'feedbax:widget:trustrestore') {
            // The visitor asked for hidden proof back, through the widget's header menu. Only a group
            // name travels and only the three known ones are acted on, so a forged message can at most
            // put back a surface this widget was configured to show anyway.
            trust.applyTrustRestore(event.data.group);
        } else if (event.data.type === 'feedbax:widget:inbox') {
            // The frame's word on waiting replies: the peek token for the next page load and the newest
            // reply. Every field is validated in the inbox surface, and the card only opens a conversation
            // the frame itself holds the key to, so a forged message can at most show a card that leads
            // nowhere.
            inbox.applyFrameInbox(event.data);
        } else if (event.data.type === 'feedbax:widget:nudges') {
            proactive.applyLiveNudges(event.data);
        } else if (event.data.type === 'feedbax:widget:trust') {
            // The trust surface was saved one card below the bubbles, and rides the same broadcast.
            revalidation.applyLiveTrust(event.data);
        } else if (event.data.type === 'feedbax:widget:branding') {
            // Both looks travel, and the repaint picks the one this page is in — a contractor saving a
            // dark accent while the host page is light must not see the light launcher change.
            const accent = isHexColor(event.data.accent) ? event.data.accent : defaultAccent;
            const accentDark = isHexColor(event.data.accentDark) ? event.data.accentDark : accent;

            options.accent = accent;
            options.contrast = isHexColor(event.data.contrast) ? event.data.contrast : contrastFor(accent);
            options.accentDark = accentDark;
            options.contrastDark = isHexColor(event.data.contrastDark)
                ? event.data.contrastDark
                : contrastFor(accentDark);
            // The panel's outline wears the soft variant — a save that clears the accent has to take
            // the outline back to the neutral with it, so an absent value is null, never the old one.
            options.tint = isHexColor(event.data.tint) ? event.data.tint : null;

            // The launcher's gradient ends ride the same save, and by the same rule: absent means the
            // circle goes back to the flat accent, so a switched-off gradient disappears without a reload.
            options.gradient = isHexColor(event.data.gradient) ? event.data.gradient : null;
            options.gradientDark = isHexColor(event.data.gradientDark) ? event.data.gradientDark : null;

            // The look rides the same save, and the corner has to follow it: without this the frame
            // re-themed itself while launcher, bubbles and trust cards kept the look the page was
            // loaded in — visible only after a reload. A host override taken earlier is kept, not
            // dropped: whether it speaks is effectiveTheme's call, so saving the scheme over to API
            // picks up the call the page already made instead of stranding the widget on light.
            const savedTheme = event.data.theme;
            if (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'auto'
                || savedTheme === 'api') {
                options.theme = savedTheme;
            }

            controls.repaintForTheme();

            if (options.theme === 'api' || controls.hostOverrideApplies()) {
                // The frame leaves 'api' unresolved (its own applyTheme knows no such look) — only this
                // side knows the page's call, so it answers with the resolved value, fallback included.
                // A stage's call outranks the saved look as well, so the frame gets the page's answer back.
                controls.postThemeToFrame();
            }

            if (event.data.side === 'left' || event.data.side === 'right') {
                revalidation.applyLiveSide(event.data.side);
                inbox.applyLiveSide(event.data.side);
            }
        }
    });
}

/* The bootstrap, and the only code here that runs on its own. Everything above is a declaration.
 * The snippet check that used to be two early returns at the top of the file lives here now — same
 * two conditions, and this is the one place that acts on them.
 *
 * Behind the page's own load, all of it (see afterPageLoad): the host page must be done before this
 * file touches it, or the widget's first requests and elements turn up in the page's load time — and in
 * the browser's tab spinner, which is where a visitor decides whether a site is slow. */
if (snippetInstalled) {
    afterPageLoad(function () {
        try {
            analytics.start();
        } catch { /* analytics must never delay or break the launcher */ }

        fetchConfig().then(function (config) {
            if (runtime.wrongHost) {
                return; // reportWrongHost said what has to change; rendering would only half work.
            }

            init(config);
        });
    });
}
